import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { dateUtils } from 'utils';
import { StatusLabel, HtmlRender } from 'components/elements';
import { useFetchProviderProfile, useOrganization } from 'hooks';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function AppointmentDetail({
  startDateTime,
  endDateTime,
  roomFullNameWithoutFees,
  healthcareServiceName,
  providerName,
  appointmentStatus,
  providerId,
  voiceAccessNumber,
  organizationNumber,
}) {
  const { t, i18n } = useTranslation('labels');
  const classes = useStyles();
  const startDate = useMemo(() => dateUtils.fullFormatDateTimeString(startDateTime), [startDateTime]);
  const startTime = useMemo(() => dateUtils.formatTimeString(startDateTime), [startDateTime]);
  const endTime = useMemo(() => dateUtils.formatTimeString(endDateTime), [endDateTime]);
  const { data: providerData } = useFetchProviderProfile(providerId);
  const { data: organization } = useOrganization();
  const wherebyEnabled = organization.features.wherebyIntegration;

  return (
    <Container className={classes.appointmentDetail} data-testid="appointment-detail">
      <Box className={classes.service}>
        <div className={classes.serviceInfoWrapper}>
          <Typography data-testid="appointment-service" className={classes.healthcareServiceName} variant="h4" color="text" component="h2">
            {healthcareServiceName}
          </Typography>{' '}
          <Typography data-testid="appointment-provider" className={classes.providerName} variant="body1" color="textSecondary" component="p">
            {providerName}
          </Typography>{' '}
          {providerData?.qualificationDetails && (
            <div className={classes.qualificationsWrapper}>
              {providerData.qualificationDetails.map(
                (qualification) =>
                  qualification?.descriptionEn && (
                    <Typography className={classes.providerTag} variant="body2" component="span" key={qualification.descriptionEn}>
                      {qualification[`description${i18n.language.charAt(0).toUpperCase() + i18n.language.slice(1)}`]?.length
                        ? qualification[`description${i18n.language.charAt(0).toUpperCase() + i18n.language.slice(1)}`]
                        : qualification.descriptionEn}
                    </Typography>
                  ),
              )}
            </div>
          )}
        </div>
      </Box>
      <Box className={classes.dateWrapper}>
        <Typography data-testid="appointment-start-date" className={classes.appointmentStartDate} variant="h6" color="text" component="div" noWrap>
          {startDate}
        </Typography>
        <Typography data-testid="appointment-time" className={classes.appointmentTime} variant="subtitle1" color="textSecondary" component="span" noWrap>
          {startTime} - {endTime}
        </Typography>
        <Typography data-testid="appointment-room" className={classes.roomFullName} variant="body1" color="textSecondary" component="p">
          {roomFullNameWithoutFees}
        </Typography>
        <StatusLabel status={appointmentStatus} classes={classes.statusLabel} />
        {wherebyEnabled && (
          <div>
            <div className={classes.dialinCodes}>
              <Typography variant="span" component="span">
                {`${t('appointment:email.content.you_may_use_dial_in_option').replace('%{brand_bridge_number}', '')}:`}
              </Typography>{' '}
              <Typography className={classes.guideLink} sx={{ fontWeight: 'bold' }} variant="span" component="span">
                {organizationNumber}
              </Typography>
            </div>
            <div>
              <Typography variant="span" component="span">
                {`${t('account_access_number')}:`}
              </Typography>{' '}
              <Typography className={classes.guideLink} sx={{ fontWeight: 'bold' }} variant="span" component="span">
                {voiceAccessNumber}
              </Typography>
            </div>
          </div>
        )}
      </Box>
      {providerData?.instructions && (
        <Box className={classes.providerInstructionsWrapper}>
          <Typography sx={{ fontWeight: 'bold' }} variant="h6" color="text" component="div">
            {t('provider_instructions')}
          </Typography>
          <Typography variant="body1" color="textSecondary" component="p">
            <HtmlRender data={providerData.patientLanguageInstructions} htmlProfile />
          </Typography>
        </Box>
      )}
    </Container>
  );
}

AppointmentDetail.propTypes = {
  startDateTime: PropTypes.string.isRequired,
  endDateTime: PropTypes.string.isRequired,
  roomFullNameWithoutFees: PropTypes.string.isRequired,
  providerName: PropTypes.string.isRequired,
  healthcareServiceName: PropTypes.string,
  appointmentStatus: PropTypes.string,
  providerId: PropTypes.string,
  voiceAccessNumber: PropTypes.string,
  organizationNumber: PropTypes.string,
};

AppointmentDetail.defaultProps = {
  healthcareServiceName: '',
  appointmentStatus: '',
  providerId: '',
  voiceAccessNumber: '',
  organizationNumber: '',
};

export default AppointmentDetail;
